import React, { useState, useRef, useEffect } from "react";
import { SFProgressBar, SFUseStyled, SFStack } from "@sf-core";
import WelcomeUser from "./welcomeUserStep";
import JobRoleSelection from "./jobRoleSelectionStep";
import NavigationButtons from "./components/navigationButtons";
import SetupCompletion from "./setupCompletionStep";
import skillSelection from "./skillSelectionStep";
import JobOfInterestSelection from "./jobOfInterestSelectionStep";
import SkillGoalsSelection from "./skillGoalsSelectionStep";
import { useSelector, useDispatch } from "react-redux";
import { profileActions } from "../../redux/reducers/profileReducer";
import "./components/slide.scss";
import { PROFILE_STEPS } from "../../constants/profileSteps";

const { styled } = SFUseStyled();

const Wrapper = styled("div")(({ theme }) => ({
  backgroundColor: "rgba(26, 26, 36, 1)",
  backgroundImage:
    "linear-gradient(108deg, rgba(121, 116, 128, 0.00) 12.64%, rgba(116, 116, 128, 0.25) 30.32%, rgba(116, 116, 128, 0.15) 48.33%, rgba(116, 116, 128, 0.25) 66.02%, rgba(116, 116, 128, 0.00) 83.69%)",
  height: "100%",
  width: "100%",
  position: "relative",
  overflow: "hidden",
}));

const StepContainer = styled("div")(({ theme }) => ({
  height: "100%",
  width: "100%",
}));

const StyledVideo = styled("video")({
  width: "100%",
  height: "270px",
  objectFit: "cover",
  opacity: 0.03,
  position: "absolute",
  top: 0,
});

const STyledStack = styled(SFStack)(({ theme }) => ({
  position: "fixed",
  top: 80,
  left: 0,
  width: "100%",
}));

const ProfileStepper = (props) => {
  const dispatch = useDispatch();
  const [progressValue, setProgressValue] = useState(0);
  const [animation, setAnimation] = useState("");
  const [disableNext, setDisableNext] = useState(false);
  const activeStep = useSelector((state) => state.profile.activeStep);
  const getVideoUrl = useSelector(
    (state) =>
      state.session.tenantSettings.popupCelebrationThumbnails.courseCompletion
  );
  // Flag to check if the skill proficiency levels are enabled
  const skillProficiencyLevelsEnabled = useSelector(
    (state) =>
      state.session.tenantSettings.lxpUserProfileSkillProficiencyLevelsEnabled
  );
  const currentComponentRef = useRef(null);
  const saveUserProfileProgress = useSelector(
    (state) => state.profile.saveUserProfileProgress
  );

  // Save user profile progress
  const onSaveAndExit = async () => {
    if (currentComponentRef?.current) {
      // Call onStepClose function of the current component and wait for it to finish
      let goStepBack = false;
      let stepCompleted = false;
      await currentComponentRef.current.onStepClose(goStepBack, stepCompleted);
      dispatch(profileActions.setState({ saveUserProfileProgress: false }));
    }
    // Exit ProfileStepper
    props.onProfileExit();
  };

  useEffect(() => {
    // Function to run when the user clicks on the "Save and Exit" button
    if (saveUserProfileProgress) onSaveAndExit();
  }, [saveUserProfileProgress]);

  // Array of all the components of the stepper
  const stepComponents = [
    WelcomeUser,
    JobRoleSelection,
    skillSelection,
    JobOfInterestSelection,
    SkillGoalsSelection,
    SetupCompletion,
  ];

  // Get the current component to be shown based on the active step
  const CurrentComponent = stepComponents[activeStep];

  // Navigate to the previous or next step
  const navigateStepBack = () => {
    if (activeStep > PROFILE_STEPS.WELCOME) setActiveStep(activeStep - 1);
  };
  const navigateStepFront = () => {
    if (activeStep < PROFILE_STEPS.COMPLETION) setActiveStep(activeStep + 1);
  };

  const setActiveStep = (step) => {
    //Check if user is going to next step or previous step to set animation of unmounting component
    let nextStep = step > activeStep;
    if (nextStep) setAnimation("slideOutUp");
    else setAnimation("slideOutDown");
    setTimeout(() => {
      setAnimation("");
      //Change active step to show next slide
      dispatch(
        profileActions.setState({
          activeStep: step,
        })
      );
      //Animate the mounting component
      if (nextStep) setAnimation("slideInUp");
      else setAnimation("slideInDown");
      setTimeout(() => {
        setAnimation("");
      }, 500);
    }, 200);
  };

  // Set when the Progress bar, the Navigation buttons and the Background video should be shown
  const showProgressBar =
    progressValue == 0 || progressValue == 100 ? false : true;
  const showNavigationButtons = activeStep != PROFILE_STEPS.WELCOME;
  const showVideo = activeStep == PROFILE_STEPS.COMPLETION;

  return (
    <Wrapper>
      {showVideo && <StyledVideo autoPlay loop muted src={getVideoUrl} />}

      <StepContainer className={`${animation}`}>
        <CurrentComponent
          key={activeStep}
          ref={currentComponentRef}
          navigateStepBack={navigateStepBack}
          navigateStepFront={navigateStepFront}
          setProgressValue={setProgressValue}
          setDisableNext={setDisableNext}
          skillProficiencyLevelsEnabled={skillProficiencyLevelsEnabled}
        />
      </StepContainer>

      {showProgressBar && (
        <STyledStack>
          <SFProgressBar value={progressValue} narrow />
        </STyledStack>
      )}
      {showNavigationButtons && (
        <NavigationButtons
          currentComponentRef={currentComponentRef}
          disableNext={disableNext}
        />
      )}
    </Wrapper>
  );
};

export default ProfileStepper;
